/**
 * Shared legal body — used by /terminos and /privacidad.
 * Contains the Commercial Intelligence consent block plus
 * audience / analytics / attribution / sponsor chapters.
 */
interface LegalBodyProps {
  variant: "terms" | "privacy";
}

const CHAPTERS: { title: string; body: string }[] = [
  {
    title: "Audience intelligence & engagement analytics",
    body: "Analizamos métricas agregadas de uso, navegación, interacción con experiencias en vivo, participación en funcionalidades dinámicas y patrones de engagement para mejorar la plataforma y entregar contenido relevante.",
  },
  {
    title: "Sponsor measurement & campaign performance",
    body: "Medimos el rendimiento de experiencias patrocinadas, promociones contextuales, campañas activas y colocaciones de marca dentro de la plataforma de forma agregada y anonimizada.",
  },
  {
    title: "Attribution systems & affiliate integrations",
    body: "Atribuimos tráfico, conversiones y participación a partners, influencers, creadores y socios comerciales autorizados para reportar correctamente el rendimiento de cada integración.",
  },
  {
    title: "Behavioral insights & personalization",
    body: "Personalizamos contenido, estadísticas, predicciones, narrativas y experiencias deportivas en función de tus preferencias, deportes favoritos, equipos seguidos y comportamiento dentro de la app.",
  },
  {
    title: "Real-time sports experiences & partner ecosystems",
    body: "Habilitamos experiencias en tiempo real con socios deportivos, operadores autorizados, medios y plataformas tecnológicas integradas para enriquecer la cobertura sin comprometer tu identidad personal.",
  },
  {
    title: "Aggregated reporting & anonymized analytics",
    body: "Generamos reportes agregados, métricas anonimizadas, modelos analíticos y tendencias de audiencia que pueden ser utilizados con fines comerciales, estratégicos o de mejora de producto.",
  },
  {
    title: "Geo-aware experiences & third-party integrations",
    body: "Adaptamos funcionalidades, promociones y contenidos según la jurisdicción, cumpliendo regulaciones locales e integrando servicios de terceros autorizados cuando aporta valor a la experiencia.",
  },
];

const CONSENT_BLOCK = `Al crear una cuenta o continuar utilizando VerseSport™, el usuario reconoce y acepta los Términos de Servicio y la Política de Privacidad de la plataforma, incluyendo el tratamiento de información relacionada con actividad deportiva, interacción digital, preferencias de contenido, métricas de uso, comportamiento dentro de la plataforma, navegación, participación en experiencias interactivas, engagement social, respuestas a funcionalidades dinámicas y uso general de los servicios ofrecidos por VerseSport™.`;

const AUTH_USES = [
  "personalización de contenido, estadísticas y experiencias deportivas;",
  "optimización de funcionalidades, sistemas predictivos y herramientas de inteligencia deportiva;",
  "medición de rendimiento de campañas, promociones y experiencias patrocinadas;",
  "atribución de tráfico, conversiones y rendimiento de afiliados, socios e influencers;",
  "elaboración de reportes estadísticos, métricas comerciales y análisis agregados de audiencia;",
  "mejora de seguridad, estabilidad, prevención de fraude y cumplimiento regulatorio;",
  "integración de funcionalidades proporcionadas por terceros autorizados;",
  "segmentación contextual y optimización de experiencias digitales relacionadas con deportes, entretenimiento y contenido interactivo.",
];

const COMMERCIAL_BLOCK = `El usuario entiende y acepta que VerseSport™ podrá generar, utilizar, licenciar, comercializar, compartir o explotar comercialmente reportes agregados, métricas anonimizadas, modelos analíticos, tendencias de comportamiento, datos estadísticos, inteligencia de audiencia, métricas de engagement y análisis de interacción derivados del uso de la plataforma, siempre conforme a la legislación aplicable y sin que ello implique necesariamente la divulgación pública de información personal identificable.`;

const PARTNER_BLOCK = `Asimismo, el usuario reconoce que determinadas funcionalidades, contenidos, promociones o experiencias pueden estar patrocinadas, afiliadas o vinculadas con socios comerciales, anunciantes, operadores autorizados, marcas deportivas, medios, plataformas tecnológicas o terceros estratégicos, incluyendo experiencias deportivas, estadísticas avanzadas, promociones contextuales y herramientas de interacción en tiempo real.`;

const AGE_BLOCK = `En jurisdicciones donde corresponda, el usuario declara ser mayor de edad para acceder a funcionalidades, promociones o contenidos restringidos relacionados con experiencias deportivas patrocinadas o integraciones de terceros autorizados.`;

const PREFS_BLOCK = `El usuario podrá administrar determinadas preferencias de privacidad, comunicaciones y personalización desde las configuraciones de la cuenta, sujeto a las limitaciones operativas, regulatorias y técnicas aplicables.`;

export function LegalBody({ variant }: LegalBodyProps) {
  return (
    <div className="space-y-5 text-sm leading-relaxed text-foreground/90">
      <p>{CONSENT_BLOCK}</p>
      <div>
        <p className="mb-2">
          El usuario autoriza a VerseSport™ y sus afiliados tecnológicos, comerciales, analíticos,
          publicitarios y estratégicos a utilizar dicha información para:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-foreground/80">
          {AUTH_USES.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      </div>
      <p>{COMMERCIAL_BLOCK}</p>
      <p>{PARTNER_BLOCK}</p>
      <p>{AGE_BLOCK}</p>
      <p>{PREFS_BLOCK}</p>

      <section className="mt-6 border-t border-border/40 pt-5">
        <h2 className="text-lg font-black text-foreground">
          {variant === "privacy"
            ? "Capítulos de tratamiento de datos"
            : "Capítulos comerciales y de integración"}
        </h2>
        <div className="mt-3 space-y-4">
          {CHAPTERS.map((c) => (
            <div key={c.title}>
              <h3 className="text-sm font-black uppercase tracking-wide text-primary">{c.title}</h3>
              <p className="mt-1 text-sm text-foreground/85">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {variant === "privacy" && (
        <section className="mt-6 rounded-lg border border-border/60 bg-accent/10 p-4 text-xs text-muted-foreground">
          Cumplimos con GDPR, CCPA y marcos regionales equivalentes. Puedes solicitar acceso,
          rectificación, portabilidad o eliminación de tus datos personales escribiendo a nuestro
          equipo de privacidad o desde la sección de configuración de tu cuenta.
        </section>
      )}
    </div>
  );
}